import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RecommendingItem } from '@app/recommendations/recommendations.model';
import { ResourceType } from '@app/shared/models/core-api.model';
import { camelCaseKeys } from '@app/shared/utils/property';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { TabRouterLessNavigationItem } from '@app/navigation/navigation.model';
import { TrackingLink } from '@app/insights/tracking-links-table/tracking-link.model';
import { RecommendationsService } from '@app/recommendations/services/recommendations.service';
import { TrackingLinkService } from '@app/insights/services/tracking-link.service';

export interface ShareTabsResolve {
  item: RecommendingItem;
  url: string;
  hasPermissionToCreateTrackableLinks: boolean;
  isEditing: boolean;
  initiator: HTMLElement;
  description: string;
  resourceId: any;
  resourceType: ResourceType;
  resourceCtrl: string;
  // preSelectedUser will only exist when sharing an opportunity with a specific user from /career/opportunities/browse/{{opportunityId}}/discover
  preSelectedUser?: RecommendedUser[];
  goToUrlAfterCreate?: string;
  link: TrackingLink;
  isSharingNewAssignment: boolean;
}

export interface RecommendedUser {
  userProfileKey: string | number;
  name: string;
  email?: string;
  picture?: string;
}

@Component({
    selector: 'dgx-share-tabs-modal',
    templateUrl: './share-tabs-modal.component.html',
    standalone: false
})
export class ShareTabsModalComponent implements OnInit {
  @Input() public hasPermissionToCreateTrackableLinks: boolean;
  @Input() public goToUrlAfterCreate: string;
  @Input() public hideHeader?: boolean;
  @Input() public initiator: HTMLElement;
  @Input() public isEditing: boolean;
  @Input() public item: RecommendingItem;
  @Input() public description: string;
  @Input() public resourceId: number;
  @Input() public resourceType: ResourceType;
  @Input() public resourceCtrl: string;
  // preSelectedUser will only exist when sharing an opportunity with a specific user from /career/opportunities/browse/{{opportunityId}}/discover
  @Input() public preSelectedUser: RecommendedUser[] = [];
  @Input() public url: string;
  @Input() public isSharingNewAssignment: boolean = false;

  @ViewChild('trackable', { static: true })
  public trackableTemplate: TemplateRef<any>;
  @ViewChild('share', { static: true })
  public shareTemplate: TemplateRef<any>;

  public isOrgContent: boolean = false;
  public displayType: string;
  public inviteUrl: string;
  public heading: string;
  public i18n = this.translate.instant([
    'Core_Share',
    'TrackableLinkCtrl_AutoEnroll',
    'A11y_SecondaryPagesNavType',
    'recommendationForm_RecommendItem_Tag',
    'recommendationForm_RecommendItem_Article',
    'recommendationForm_RecommendItem_Assessment',
    'recommendationForm_RecommendItem_Book',
    'recommendationForm_RecommendItem_Course',
    'recommendationForm_RecommendItem_Event',
    'recommendationForm_RecommendItem_Opportunity',
    'recommendationForm_RecommendItem_Pathway',
    'recommendationForm_RecommendItem_Target',
    'recommendationForm_RecommendItem_Episode',
    'recommendationForm_RecommendItem_Post',
    'recommendationForm_RecommendItem_Task',
    'recommendationForm_RecommendItem_Video',
    'recommendationForm_RecommendItem_Academy',
    'recommendationForm_AssignContent',
  ]);
  public resolve: ShareTabsResolve;
  public tabList: TabRouterLessNavigationItem[];
  public isSharingNewPathwayOrTarget = false;

  constructor(
    private activeModal: NgbActiveModal,
    private translate: TranslateService,
    private recommendationsService: RecommendationsService,
    private trackingLinkService: TrackingLinkService
  ) {}

  public get itemForForm(): RecommendingItem {
    const shouldRecommendationFormRerender =
      this.isSharingNewAssignment &&
      this.isSharingNewPathwayOrTarget &&
      this.hasPermissionToCreateTrackableLinks;
    return shouldRecommendationFormRerender ||
      (!this.isSharingNewAssignment && this.resolve?.item)
      ? this.resolve?.item
      : null;
  }

  public get showNavTabs(): boolean {
    return this.isSharingNewAssignment
      ? this.tabList.length > 1
      : this.hasPermissionToCreateTrackableLinks;
  }

  public ngOnInit(): void {
    // TODO: Remove this when we are no longer passing pascal-cased items in anywhere
    this.item = camelCaseKeys(this.item);
    this.heading = this.getTranslatedHeading();

    // this being set from the recommendationSvc.
    this.isOrgContent = this.item?.isOrgContent;
    // add everything to a mock resolve object for our AJS children
    this.resolve = this.getInitialResolveObject();
    this.tabList = this.getTabList();
  }

  public close(response?) {
    this.activeModal.close(response);
  }

  public dismiss() {
    this.activeModal.dismiss();
  }

  /**
   * This is called when creating a new assignment on the manager assignments page
   * @param $event
   */
  public onContentSelectedForNewAssignment($event) {
    this.isOrgContent = $event.item ? $event.item?.isOrgContent : null;
    this.hasPermissionToCreateTrackableLinks = $event.item
      ? $event.hasPermissionToCreateTrackableLinks
      : null;
    this.isSharingNewPathwayOrTarget =
      $event.item?.resourceType === 'Pathway' ||
      $event.item?.resourceType === 'Target';
    this.resolve = this.getResolveObjectForNewAssignment(
      $event.item,
      $event.enhancedItemResourceId
    );
    this.tabList = this.getTabList();
  }

  private getTranslatedHeading() {
    const translationKey = this.isSharingNewAssignment
      ? 'recommendationForm_AssignContent'
      : `recommendationForm_RecommendItem_${this.item?.resourceType}`;
    return this.i18n[translationKey];
  }

  private getInitialResolveObject(): ShareTabsResolve {
    return {
      item: this.item,
      url: this.url,
      hasPermissionToCreateTrackableLinks:
        this.hasPermissionToCreateTrackableLinks,
      description: this.description,
      goToUrlAfterCreate: this.goToUrlAfterCreate,
      initiator: this.initiator,
      isEditing: this.isEditing,
      resourceId: this.resourceId,
      resourceType: this.resourceType,
      resourceCtrl: this.resourceCtrl,
      // TODO: Remove this when we are no longer passing pascal-cased items in anywhere
      preSelectedUser: this.preSelectedUser?.map((user) => camelCaseKeys(user)),
      link: null,
      isSharingNewAssignment: this.isSharingNewAssignment,
    };
  }

  private getResolveObjectForNewAssignment(
    selectedContentForAssignment: RecommendingItem,
    enhancedItemResourceId: number
  ): ShareTabsResolve {
    // grab properties from item
    const resourceType = selectedContentForAssignment?.resourceType;
    const inviteUrl = selectedContentForAssignment?.inviteUrl;
    const resourceCtrl = this.trackingLinkService.resourceCtrlFor(resourceType);
    const url = inviteUrl
      ? inviteUrl
      : selectedContentForAssignment
        ? this.recommendationsService.getShareUrl(
            selectedContentForAssignment as RecommendingItem
          )
        : null;
    const isPathways = resourceType === 'Pathway';
    const insightsLink = !selectedContentForAssignment
      ? null
      : isPathways
        ? `${selectedContentForAssignment.publicUrl}/insights/links`
        : `/plan/${selectedContentForAssignment.resourceId}/insights`;

    const description = !selectedContentForAssignment
      ? null
      : this.translate.instant('TrackableLinkCtrl_LinkDescription', {
          aTagStart: `<a href="${insightsLink}" class="link">`,
          aTagEnd: '</a>',
        });
    return {
      item: selectedContentForAssignment,
      url,
      hasPermissionToCreateTrackableLinks:
        this.hasPermissionToCreateTrackableLinks,
      description,
      goToUrlAfterCreate: isPathways ? insightsLink : '',
      isEditing: this.isEditing,
      initiator: this.initiator,
      resourceId: enhancedItemResourceId,
      resourceType: resourceType,
      resourceCtrl: resourceCtrl,
      preSelectedUser: this.preSelectedUser?.map((user) => camelCaseKeys(user)),
      link: null,
      isSharingNewAssignment: this.isSharingNewAssignment,
    };
  }

  private getTabList(): TabRouterLessNavigationItem[] {
    let tabList = [
      {
        name: this.i18n.Core_Share,
        nameId: 'share',
        template: this.shareTemplate,
      },
    ];

    if (this.hasPermissionToCreateTrackableLinks && this.isOrgContent) {
      tabList = [
        ...tabList,
        {
          name: this.i18n.TrackableLinkCtrl_AutoEnroll,

          nameId: 'trackable',
          template: this.trackableTemplate,
        },
      ];
    }

    return tabList;
  }
}
